import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef } from '@angular/core';
import { LoanCard } from '../models/loan-card';
import { environment } from '../../../environments/environment';
import { ApplicationService } from 'app/application/application.service';
import { DeviceDetectService } from 'app/common/device-detect/device-detect.service';
import { CancellationReasons, Application, ActiveApplication, GiftCardDetails, GiftCardGenerationStatus, EmiOption, States } from 'app/application/application';
import { Loader, LoaderService } from 'app/common/loader/loader.service';
import { TokenService } from 'app/token/token.service';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { Observable } from 'rxjs/Observable';
import { EventDetails } from '../../common/tracking/aws-tracker';
import { AwsTrackingService } from 'app/common/tracking/aws-tracking.service';
import { EmailMobileService } from '../../customer/email-mobile/email-mobile.service';
import { Router } from '@angular/router';
import { CardType } from '../models/card-type.enum';
import { HttpParams } from '@angular/common/http';
import { PostAccept } from 'app/risk/risk';
import { MerchantSetting } from '../../landing-pages/gift-card/gift-card';
import { ApiDataService } from '../api-data.service';
import { RupeeCurrencyPipe } from 'app/common/pipes';
import { retry } from 'rxjs/operators';

declare let $;
declare var Materialize: any;

@Component({
  selector: 'zest-loan-status-card',
  templateUrl: './loan-status-card.component.html',
  styleUrls: ['./loan-status-card.component.scss']
})
export class LoanStatusCardComponent implements OnInit {

  @Input() loanCard: LoanCard;
  @Input() states: States;
  @Input() clState: PostAccept;
  @Input() currentApplication: ActiveApplication;
  @Input() approvedApplications: ActiveApplication[] = [];

  @Output() refresh: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() voucherView: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('other', { static: false }) otherReasonElement: ElementRef;

  isDesktop: boolean;
  showAllLoans: boolean;
  s3Url = environment.s3BucketUrl;
  CancellationReasons = CancellationReasons;
  cancellationReason = '';
  otherReason = '';
  application: Application;
  selectedGCApplications: ActiveApplication[] = [];
  lastestApprovedApplication: ActiveApplication;
  disableCopy: boolean;
  generatedGiftCardDetails: GiftCardDetails[] = new Array<GiftCardDetails>();
  someOfflineGiftCardGenerationSucceed: boolean;
  generationComplete: boolean;
  allGiftCardsGenerated: boolean;
  someOfflineApplications: boolean;
  someGiftCardGenerationFailed: boolean;
  someOfflineGiftCardGenerationFailed: boolean;
  showUpsell: boolean;
  upsellAmount: number;
  counterOfferAmount: number;
  counterOfferEmiPLans: EmiOption[];
  availableCreditLimitAmount: number;
  offlineMerchants: string[] = [];
  merchantSettings: MerchantSetting[];
  showVouchers: boolean;
  showUpdateView: boolean;
  insuranceAmount: number;
  useNewGCGenFlow: boolean;

  constructor(
    private applicationService: ApplicationService,
    private deviceService: DeviceDetectService,
    private loaderService: LoaderService,
    private tokenService: TokenService,
    private awsTrackingService: AwsTrackingService,
    private emailMobileService: EmailMobileService,
    private apiDataService: ApiDataService,
    private rupeePipe: RupeeCurrencyPipe,
    private router: Router
  ) { }

  ngOnInit() {
    this.apiDataService.getNewGCVoucherGenerationSwitch().subscribe(response => {
      if (response && response.useNewFlow) {
        this.useNewGCGenFlow = response.useNewFlow;
      } else {
        this.useNewGCGenFlow = false;
      }
    }, error => {
      console.log('GC Gen. Flow json fetch error', error);
      this.useNewGCGenFlow = false;
    });
    this.isDesktop = this.deviceService.isDesktop();
    this.application = this.applicationService.getApplication();
    this.merchantSettings = this.tokenService.getMerchantSettings();
    if (this.merchantSettings) {
      this.merchantSettings.forEach(merchant => {
        if (merchant.isOffline) {
          this.offlineMerchants.push(merchant.merchantId);
        }
      });
    }

    if (this.loanCard.cardType === CardType.Approved || this.loanCard.cardType === CardType.OrderComplete) {
      this.checkForUpSellCondition(this.application, this.states, this.clState);
      this.checkForInsuranceAmount();
    } else if (this.loanCard.cardType === CardType.CounterOffer) {
      this.counterOfferAmount = this.clState.credit_limit.available_amt;
      this.applicationService.getRecommendedAndOtherEMIPlans(this.application.MerchantId, this.counterOfferAmount, null).subscribe((response) => {
        this.counterOfferEmiPLans = (response.OtherEmiOptions ? response.RecommendedEmiOptions.concat(response.OtherEmiOptions) : response.RecommendedEmiOptions).sort(this.applicationService.sortEmiOptionsByNumberOfMonths);
        this.loanCard = {
          ...this.loanCard,
          merchantName: this.application.MerchantName,
          loanAmount: this.counterOfferAmount,
          tenure: this.counterOfferEmiPLans[0].NumberOfMonths,
          emi: this.counterOfferEmiPLans[0].TotalMonthlyAmount
        };
      });
    }
    this.selectedGCApplications.push(this.currentApplication);
  }

  checkForInsuranceAmount() {
    this.apiDataService.getInsuranceFee(this.application.ApplicationId, this.application.MerchantId, this.application.LoanAmount, this.application.NumberOfInstallments).subscribe((response) => {
      if (response.status) {
        const { insuranceFee } = response.data;
        this.insuranceAmount = insuranceFee;
        this.loanCard.cardInfo = this.loanCard.cardInfo + ` The insurance fee for this loan is ${this.rupeePipe.transform(this.insuranceAmount)}.`;
      }
    });
  }

  checkForUpSellCondition(application, states, clState) {
    const approvedClStatusCodes = ['ZMRSK200', 'ZMRSK201', 'ZMRSK202', 'ZMRSK400'];
    const merchantName = application.MerchantName.toLowerCase();
    const currentMerchantSetting = this.merchantSettings.find(merchant => (merchant.partnerName.toLowerCase() === this.application.MerchantName.toLowerCase()));
    const maxVoucherAmount = currentMerchantSetting.maxLimit;
    const riskApproved = clState && clState.status_code && approvedClStatusCodes.indexOf(clState.status_code) > -1;
    const isDpAfterRiskApproval = this.applicationService.getDpConfig();

    if ((merchantName === 'flipkart' || merchantName === 'amazonpay') && !isDpAfterRiskApproval && environment.giftCards[merchantName]) {
      this.availableCreditLimitAmount = clState && clState.credit_limit ? Math.floor(Number(clState.credit_limit.available_amt)) : 0;
      this.upsellAmount = this.application.LoanAmount + this.availableCreditLimitAmount;
      if (this.upsellAmount > maxVoucherAmount) {
        this.upsellAmount = maxVoucherAmount;
      }
      if (!states.loan.IsLoanAmountChangeAccepted && riskApproved && (this.upsellAmount - this.application.LoanAmount) >= 2000) {
        this.applicationService.getFeatureVersion('UPSELL').subscribe(res => {
          this.applicationService.tagFeatureToCustomer(res.featureId, res.version).subscribe();
          if (res.version !== 'N/A') {
            this.showUpsell = true;
            this.logEvent('upsell_available', 'event', 'loaded');
          }
        });
      }
    }
  }

  continueLoan() {
    if (this.loanCard.cardType === CardType.CounterOffer) {
      this.continueCounterOffer(this.counterOfferEmiPLans[0]);
    } else {
      this.applicationService.goToNextRoute('/loggedinredirect');
    }
  }

  bulkCancel(applications) {
    this.selectedGCApplications = [...applications];
    this.toggleConfirmCancelLoan('open');
  }

  bulkGenerate(applications) {
    this.selectedGCApplications = [...applications];
    this.toggleAllLoansView('close');
    this.generateVoucher(this.selectedGCApplications);
  }

  toggleCardInfo(state) {
    $('#card-info').modal(state);
    this.logEvent('voucher_card_info' + state + '_click');
  }

  toggleConfirmCancelLoan(state) {
    $('#confirm-cancel-loan').modal(state);
    if (state === 'close') {
      this.cancellationReason = '';
      const props = {
        Action: 'No',
        View: 'CancelLoan'
      };
      this.logEventWithCustomProps('ViewAction', 'event', 'triggered', props);
    }
    if (state === 'open') {
      const props = {
        Action: 'Cancel',
        View: 'OCDLoanApproved'
      };
      this.logEventWithCustomProps('ViewAction', 'event', 'triggered', props);
      const modalProps = {
        Context: 'OCDLoanApproved',
        LastAction: 'Cancel',
        View: 'CancelLoan'
      };
      this.logEventWithCustomProps('ViewVisited', 'event', 'triggered', modalProps);
    }
  }

  toggleConfirmGetVoucher(state) {
    $('#confirm-get-voucher').modal(state);
    if (state === 'open') {
      const props = {
        Action: 'GetVoucher',
        View: 'OCDLoanApproved'
      };
      this.logEventWithCustomProps('PrimaryCTA', 'event', 'triggered', props);
      const modalProps = {
        LastAction: 'GetVoucher',
        Context: 'OCDLoanApproved',
        View: 'OCDGetVoucher'
      };
      this.logEventWithCustomProps('ViewVisited', 'event', 'triggered', modalProps);
    } else {
      const props = {
        Action: 'Close',
        View: 'OCDGetVoucher'
      };
      this.logEventWithCustomProps('ViewAction', 'event', 'triggered', props);
    }
  }

  toggleAllLoansView(state) {
    $('#all-loans').modal(state);
    this.logEvent('all_loan_' + state + '_click');
  }

  toggleVouchersView(state) {
    if (state === 'open') {
      this.showVouchers = true;
      this.voucherView.emit(true);
    } else if (state === 'close') {
      this.showVouchers = false;
      this.voucherView.emit(false);
      this.refreshApplications();
    }
  }

  toggleUpSellView(state) {
    $('#up-sell').modal(state);
    if (state === 'open') {
      this.showUpdateView = true;
    } else {
      this.showUpdateView = false;
    }
    this.logEvent('upsell_' + state + '_click');
  }

  toggleCounterOfferView(state) {
    $('#counter-offer').modal(state);
    if (state === 'open') {
      this.showUpdateView = true;
    } else {
      this.showUpdateView = false;
    }
    this.logEvent('counter_offer_' + state + '_click');
  }

  checkIfOther() {
    setTimeout(() => {
      if (this.cancellationReason === 'Other') {
        this.otherReasonElement.nativeElement.focus();
      }
    }, 0);
  }

  cancelApplications(applications: ActiveApplication[], cancellationReason, otherReason = '') {
    this.toggleConfirmCancelLoan('close');
    this.tokenService.removeAllModals();
    if (applications.length) {
      this.loaderService.ShowLoaderSource.next(new Loader(true, 'We are cancelling your voucher'));
      const observablesArray: Observable<any>[] = applications.map(application => this.applicationService.changeApplicationStatusZestLife(application.ExternalId, application.MerchantId));
      forkJoin(...observablesArray).subscribe(response => {
        if (response && response.length && response[0].errorCode && response[0].errorCode !== '') {
          this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
          Materialize.toast('Could not cancel your voucher, please try again later', 5000, 'rounded', () => { this.tokenService.removeAllToasts(); });
        } else {
          Materialize.toast('Your voucher has been cancelled', 5000, 'rounded', () => { this.tokenService.removeAllToasts(); });
          this.refreshApplications();
        }
      }, error => {
        Materialize.toast('Could not cancel your voucher, please try again later', 5000, 'rounded', () => { this.tokenService.removeAllToasts(); });
        this.refreshApplications();
      });
    }
  }

  refreshApplications() {
    this.refresh.emit(true);
  }

  voucherGenFailed() {
    this.logEvent('get_voucher_failed', 'event', 'failed');
    if (!this.someOfflineApplications) {
      this.someGiftCardGenerationFailed = true;
      this.tokenService.setVoucherGenFailed();
      this.router.navigate(['/dashboard/voucher-generation-failed']);
    } else {
      this.someOfflineGiftCardGenerationFailed = true;
      Materialize.toast('Your voucher generation failed, Please try again.', 5000, 'rounded', () => { this.tokenService.removeAllToasts(); });
    }
  }

  takeWhatsappConsentFromCustomer() {
    this.applicationService.takeWhatsappConsent().pipe(retry(3)).subscribe((res) => { }, err => {
      console.error(err);
    });
  }

  generateVoucher(applications) {
    let currentVoucherApprovedApplicationsCount = 0;
    this.toggleConfirmGetVoucher('close');
    const props = {
      Action: 'Proceed',
      View: 'OCDGetVoucher'
    };
    this.logEventWithCustomProps('PrimaryCTA', 'event', 'triggered', props);
    if (applications.length > 1) {
      this.logEvent('get_voucher_bulk_click');
    } else {
      this.logEvent('get_voucher_click');
    }

    if (this.tokenService.getCustomerContact()) {
      this.takeWhatsappConsentFromCustomer();
    } else {
      this.emailMobileService.getMobile().subscribe(response => {
        if (response && response.PhoneNumber) {
          this.tokenService.setCustomerContact(response.PhoneNumber.slice(2));
          this.takeWhatsappConsentFromCustomer();
        }
      });
    }

    this.applicationService.sendDataToLogger('Application list before voucher generation', {
      applications
    }).subscribe();
    applications.forEach(application => {
      this.loaderService.ShowLoaderSource.next(new Loader(true, 'Voucher is on the way'));
      this.applicationService.createVoucherDenomination(application.ExternalId).subscribe(res => {
        if (res.errorCode) {
          this.voucherGenFailed();
          currentVoucherApprovedApplicationsCount++;
          this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
        } else {
          this.applicationService.getTokenForCreateVoucher(application.ExternalId, application.MerchantId, application.MerchantName).subscribe(response => {
            this.applicationService.createGiftCardVoucher(application.ExternalId, application.MerchantId, application.MerchantName, this.useNewGCGenFlow).subscribe(response => {
              currentVoucherApprovedApplicationsCount++;
              let vGenFailed = false;
              if (response.giftCardGenerationStatus === GiftCardGenerationStatus.SUCCESS) {
                if (!this.useNewGCGenFlow) {
                  this.disableCopy = response.disableCopy;
                  this.someOfflineGiftCardGenerationSucceed = this.someOfflineGiftCardGenerationSucceed || this.offlineMerchants.indexOf(this.application.MerchantId) !== -1;
                  response.giftCardDetails.map(voucher => voucher.merchantName = application.MerchantName);
                  this.generatedGiftCardDetails = this.generatedGiftCardDetails.concat(response.giftCardDetails);
                }
              } else {
                vGenFailed = true;
                // Voucher not generated / pending / partially generated
                this.voucherGenFailed();
              }
              if (applications.length === currentVoucherApprovedApplicationsCount) {
                this.generationComplete = true;
                this.allGiftCardsGenerated = !this.someGiftCardGenerationFailed && !this.someOfflineGiftCardGenerationFailed;
                this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
                if (!vGenFailed) {
                  this.logEvent('OCDVoucherActivated', 'event', 'triggered');
                }
                if (this.useNewGCGenFlow) {
                  this.router.navigate(['/dashboard/voucher-generation']).then(() => {
                    window.location.reload();
                  });
                } else {
                  this.toggleVouchersView('open');
                }
              }
            }, error => {
              this.voucherGenFailed();
              currentVoucherApprovedApplicationsCount++;
              this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
            });
          }, error => {
            this.voucherGenFailed();
            this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
          });
        }
      }, error => {
        this.voucherGenFailed();
        currentVoucherApprovedApplicationsCount++;
        this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
      });
    });
  }

  continueUpsell(selectedPlan: EmiOption) {
    this.toggleUpSellView('close');
    this.loaderService.ShowLoaderSource.next(new Loader(true, ''));
    this.logEvent('upsell_update_voucher_click');
    const postParams = {
      'Operations': [
        {
          'op': 'replace',
          'path': '/LoanAmount',
          'value': selectedPlan.LoanAmount
        },
        {
          'op': 'replace',
          'path': '/NumberOfInstallments',
          'value': selectedPlan.NumberOfMonths
        }
      ]
    };
    this.applicationService.patchLoanAmountToApplication(this.application.ApplicationId, postParams).subscribe(response => {
      this.applicationService.approveLoanAmountChangeCounterOffer(this.application.ApplicationId, true).subscribe(response => {
        this.refreshApplications();
        this.logEvent('upsell_update_voucher-completed', 'event', 'completed');
      }, error => {
        this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
        this.tokenService.removeAllToasts();
        Materialize.toast('Something went wrong. Please try again.', 5000, 'rounded', () => { this.tokenService.removeAllToasts(); });
        this.logEvent('upsell_update_voucher-failed', 'event', 'failed');
      });
    }, error => {
      this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
      this.tokenService.removeAllToasts();
      Materialize.toast('Something went wrong. Please try again.', 5000, 'rounded', () => { this.tokenService.removeAllToasts(); });
      this.logEvent('upsell_update_voucher-failed', 'event', 'failed');
    });
  }

  continueCounterOffer(selectedPlan: EmiOption) {
    this.loaderService.ShowLoaderSource.next(new Loader(true, ''));
    this.logEvent('counter_offer_update_voucher_click');
    const reason = this.application.BasketItems[0].Description;
    const email = this.tokenService.getCustomerDetails().email;
    this.emailMobileService.getMobile().subscribe(response => {
      const mobileNumber = Number(response.PhoneNumber.substring(2));
      const giftCardData = {
        walletId: this.application.MerchantName.toLowerCase() + 'giftcard',
        reason,
        amount: selectedPlan.LoanAmount,
        mobileNumber: mobileNumber,
        email,
        numberOfMonths: selectedPlan.NumberOfMonths
      };
      this.applicationService.createGcApplication(giftCardData).subscribe(response => {
        const url = new URL(response.url);
        const searchParams = new HttpParams({
          fromString: url.search.substring(1)
        });
        const applicationId = searchParams.get('LoanApplicationId');
        this.applicationService.getOrderDetails(applicationId).subscribe(applicationResponse => {
          this.tokenService.removeAllExceptToken();
          this.applicationService.saveApplication(applicationResponse);
          this.tokenService.setSkipDashboard(true);
          this.router.navigate(['/loggedinredirect']);
        });
      }, error => {
        this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
      });
    });
  }

  logEvent(eventName, eventType = 'event', eventValue = 'triggered') {
    const event = new EventDetails(eventName, eventType, eventValue);
    this.awsTrackingService.sendDataToFunnel(event).subscribe();
    if (eventType === 'page') {
      this.awsTrackingService.gtmPageLoadEvent(eventName);
    } else {
      this.awsTrackingService.gtmTriggerEvent(eventName);
    }
  }

  logEventWithCustomProps(eventName, eventType = 'event', eventValue = 'triggered', props = {}) {
    const event = new EventDetails(eventName, eventType, eventValue);
    this.awsTrackingService.sendDataToFunnelWithCustomProperties(event, props);
  }

  trackReason(reason: string) {
    const props = {
      Reason: reason
    };
    this.logEventWithCustomProps('OCDCancelLoan-Yes', 'event', 'triggered', props);
  }
  redirectToUGC() {
    const avialableCreditLimit = this.clState ? this.clState.credit_limit.available_amt : 0;
    const selectedMerchantSetting = this.merchantSettings.filter(merchantSettings => (merchantSettings.partnerName.toLowerCase() === this.application.MerchantName.toLowerCase()))[0];
    const data = {
      tv: this.application.MerchantName,
      sz: this.application.MerchantId,
      ue: btoa(this.states.customer.customerId),
      ew: Math.min(avialableCreditLimit, selectedMerchantSetting.maxLimit),
      ly: 'Gift Card',
      va: avialableCreditLimit,
      fx: true
    };
    this.logEvent('cl_insufficient_downsell_' + this.application.MerchantName + '_click');
    setTimeout(() => {
      this.router.navigate(['/emi/get'], { queryParams: data });
    }, 0);
  }
}
